import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Lock,
  Phone,
  Briefcase,
  User,
  CheckCircle,
  Eye,
  EyeOff,
  Link2,
  AlertTriangle,
  Handshake,
} from "lucide-react";
import { professionalInvitationAPI, tokenManager } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useUI } from "../context/UIContext";
import Button from "../components/common/Button";
import { Select } from "../components/common/FormControls";

const PROFESSION_OPTIONS = [
  { value: "CA", label: "CA (Chartered Accountant)" },
  { value: "CS", label: "CS (Company Secretary)" },
  { value: "CMA", label: "CMA (Cost Accountant)" },
  { value: "Advocate", label: "Advocate" },
  { value: "Consultant", label: "Consultant" },
  { value: "Other", label: "Other" },
];

/* ── Shared layout shell (mirrors LoginForm left panel) ── */
const FeatureRow = ({ icon, title, description }) => (
  <div className="flex items-center gap-[13px]">
    <div
      className="w-[36px] h-[36px] rounded-[9px] border flex items-center justify-center text-[16px] flex-shrink-0"
      style={{ background: 'rgba(184,151,58,.12)', borderColor: 'rgba(184,151,58,.2)' }}
    >
      {icon}
    </div>
    <div className="text-[11.5px] text-white/70">
      <strong className="text-white/90">{title}</strong> – {description}
    </div>
  </div>
);

const Shell = ({ children }) => (
  <div
    className="h-[var(--app-vh)] w-full flex overflow-hidden"
    style={{ background: "#f4f0e8" }}
  >
    <div
      className="relative hidden lg:flex h-[var(--app-vh)] flex-1 px-[52px] py-12 flex-col justify-between overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg,#0a1628 0%,#0d2a3a 40%,#0a3d2a 100%)",
      }}
    >
      {/* Decorative pattern overlay */}
      <div className="absolute inset-0" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />
      {/* Radial glow */}
      <div className="absolute -bottom-[120px] -left-[80px] w-[500px] h-[500px] pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(184,151,58,.12) 0%,transparent 70%)' }} />

      <div className="relative z-[1]">
        {/* Logo + wordmark */}
        <div className="flex items-center gap-[14px] mb-12">
          <img src="/complyv-logo.svg" alt="ComplyV" className="w-12 h-12 object-contain" />
          <div>
            <div className="text-[24px] font-bold leading-none" style={{ color: '#b8973a', fontFamily: "'Cormorant Garamond', serif" }}>ComplyV</div>
            <div className="text-[8.5px] tracking-[3px] uppercase text-white/30 mt-1">One-Stop Compliance &amp; Business Platform</div>
          </div>
        </div>

        {/* Hero text — invitation-specific */}
        <div className="text-[38px] leading-[1.15] font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Join Our<br /><span style={{ color: '#b8973a' }}>Compliance</span><br />Network
        </div>
        <p className="mt-[18px] text-[13px] leading-[1.7] text-white/55 max-w-[420px]">
          You&apos;ve been invited to collaborate on compliance documents, board resolutions, and regulatory filings — all in one secure platform.
        </p>

        {/* Feature rows */}
        <div className="mt-9 space-y-[14px]">
          <FeatureRow icon="&#129302;" title="AI Document Drafting" description="Board minutes, resolutions, affidavits with company letterhead" />
          <FeatureRow icon="&#128197;" title="AGM Due Date Engine" description="AOC-4, MGT-7, ADT-1 auto-calculated from AGM date" />
          <FeatureRow icon="&#127970;" title="MCA21 Integration" description="Fetch company data instantly with CIN or upload master export" />
          <FeatureRow icon="&#128220;" title="100+ Resolution Templates" description="Every business transaction, fully editable & printable" />
        </div>
      </div>

      {/* Stats + footer */}
      <div className="relative z-[1]">
        <div className="flex gap-8 text-center text-white">
          {[['127+', 'Companies'], ['100+', 'Doc Templates'], ['SS-1/2', 'Compliant'], ['24/7', 'AI Support']].map(([val, lbl]) => (
            <div key={lbl}><div className="text-[25px] font-bold leading-none" style={{ color: '#b8973a', fontFamily: "'Cormorant Garamond', serif" }}>{val}</div><div className="text-[9.5px] text-white/35 mt-[3px] uppercase tracking-[1.2px]">{lbl}</div></div>
          ))}
        </div>
        <div className="mt-6 text-[10.5px] text-white/20">&copy; 2026 ComplyV &middot; Companies Act 2013 &middot; LLP Act 2008 &middot; SEBI &middot; DPDP 2023</div>
      </div>
    </div>

    {/* Right panel — form */}
    <div className="w-full lg:w-[460px] h-[var(--app-vh)] bg-[#f4f0e8] overflow-y-auto px-7 md:px-12 py-10 md:py-[52px] flex items-center">
      <div className="w-full max-w-[364px] mx-auto">{children}</div>
    </div>
  </div>
);

const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useUI();
  const { setUser } = useAuth();

  const token = searchParams.get("token");

  const [phase, setPhase] = useState("verifying");
  const [errorMessage, setErrorMessage] = useState("");
  const [professionalInfo, setProfessionalInfo] = useState(null);

  const [fullName, setFullName] = useState("");
  const [profession, setProfession] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [membershipNo, setMembershipNo] = useState("");
  const [showPasswordToggle, setShowPasswordToggle] = useState(false);
  const [showConfirmToggle, setShowConfirmToggle] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  /* ── Verify token on mount ── */
  useEffect(() => {
    if (!token) {
      setErrorMessage("Invalid invitation link");
      setPhase("error");
      return;
    }
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await professionalInvitationAPI.verifyInviteToken({
        token,
      });
      if (response.success) {
        setProfessionalInfo(response.data.professional);
        // Pre-fill editable fields from invitation data
        if (response.data.professional?.fullName) {
          setFullName(response.data.professional.fullName);
        }
        if (response.data.professional?.profession) {
          setProfession(response.data.professional.profession);
        }
        setPhase("form");
      } else {
        setErrorMessage(
          response.message || "This invitation link is invalid or has expired.",
        );
        setPhase("error");
      }
    } catch (error) {
      console.error("Token verification error:", error);
      // Surface API error message if available, otherwise generic
      setErrorMessage(
        error?.message ||
          "Failed to verify invitation. Please check your link or request a new one.",
      );
      setPhase("error");
    }
  };

  /* ── Validation ── */
  const validateForm = () => {
    const errs = {};

    if (!fullName.trim()) {
      errs.fullName = "Full name is required";
    }

    if (!profession) {
      errs.profession = "Profession is required";
    }

    if (!password) {
      errs.password = "Password is required";
    } else if (password.length < 8) {
      errs.password = "Password must be at least 8 characters";
    } else {
      const strength =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/;
      if (!strength.test(password)) {
        errs.password =
          "Must include uppercase, lowercase, number, and special character";
      }
    }

    if (!confirmPassword) {
      errs.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }

    if (mobile && !/^[6-9]\d{9}$/.test(mobile)) {
      errs.mobile = "Please enter a valid 10-digit mobile number";
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setBusy(true);
    try {
      const response = await professionalInvitationAPI.completeOnboarding({
        token,
        password,
        fullName,
        profession,
        mobile: mobile || undefined,
        membershipNo: membershipNo || undefined,
      });

      if (response.success) {
        if (response.data.tokens) {
          tokenManager.setToken(response.data.tokens.accessToken);
        }
        if (response.data.user) {
          tokenManager.setUser(response.data.user);
          setUser(response.data.user);
        }
        setPhase("success");
        showToast(
          "Welcome! Your account has been set up successfully",
          "success",
        );
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 1500);
      } else {
        setFieldErrors({
          global: response.message || "Failed to complete onboarding",
        });
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      // Surface API error message if available
      setFieldErrors({
        global:
          error?.message || "Failed to complete onboarding. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  /* ── No token ── */
  if (!token) {
    return (
      <Shell>
        <div className="text-center">
          <div className="flex justify-center mb-3"><Link2 size={40} style={{ color: '#145f5f' }} /></div>
          <div
            className="text-[20px] font-bold text-[#08111f] mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Invalid Invitation Link
          </div>
          <div className="text-[13px] text-[#7a7060] mb-6 leading-relaxed">
            The invitation link is missing or malformed. Please ask your
            corporate contact to resend the invitation.
          </div>
          <Button block size="lg" onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </div>
      </Shell>
    );
  }

  /* ── Verifying ── */
  if (phase === "verifying") {
    return (
      <Shell>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#145f5f] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[13px] text-[#7a7060]">Verifying invitation…</p>
        </div>
      </Shell>
    );
  }

  /* ── Error ── */
  if (phase === "error") {
    return (
      <Shell>
        <div className="text-center">
          <div className="flex justify-center mb-3"><AlertTriangle size={40} style={{ color: '#9a564a' }} /></div>
          <div
            className="text-[20px] font-bold text-[#08111f] mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Invalid or Expired Invitation
          </div>
          <div className="mb-4 text-[12.5px] px-3 py-2 rounded-lg border bg-[#f2e9e6] border-[#e6d2cb] text-[#9a564a]">
            {errorMessage}
          </div>
          <div className="text-[13px] text-[#7a7060] mb-6 leading-relaxed">
            Invitation links expire after 7 days and are single-use. Please
            request a new invitation from your corporate contact.
          </div>
          <Button block size="lg" onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </div>
      </Shell>
    );
  }

  /* ── Success ── */
  if (phase === "success") {
    return (
      <Shell>
        <div className="text-center">
          <div className="flex justify-center mb-3"><CheckCircle size={40} style={{ color: '#4a6b3f' }} /></div>
          <div
            className="text-[22px] font-bold text-[#08111f] mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Welcome aboard!
          </div>
          <div className="mb-4 text-[12.5px] px-3 py-2 rounded-lg border bg-[#e8efe5] border-[#d3e2cb] text-[#4a6b3f]">
            Your account has been set up successfully.
          </div>
          <div className="text-[12.5px] text-[#7a7060]">
            Redirecting to your dashboard…
          </div>
        </div>
      </Shell>
    );
  }

  /* ── Main form ── */
  return (
    <Shell>
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="text-center mb-5">
          <div className="flex justify-center mb-2"><Handshake size={40} style={{ color: '#b8973a' }} /></div>
          <div
            className="text-[26px] font-bold mb-[6px]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Complete Your Profile
          </div>
          <div className="text-[12.5px] text-[#7a7060]">
            You&apos;ve been invited to join ComplyV
            {professionalInfo?.profession
              ? ` as a ${professionalInfo.profession}`
              : ""}
          </div>
        </div>

        {/* Inviter info card */}
        {professionalInfo && (
          <div
            className="mb-5 p-4 rounded-[10px] border bg-white"
            style={{ borderColor: "#e8e0d0" }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-[#f4f0e8] flex items-center justify-center">
                <User className="w-5 h-5 text-[#7a7060]" />
              </div>
              <div>
                <div className="text-[13px] font-semibold text-[#08111f]">
                  {professionalInfo.fullName}
                </div>
                <div className="text-[11.5px] text-[#7a7060]">
                  {professionalInfo.email}
                </div>
                {professionalInfo.profession && (
                  <div className="text-[11px] text-[#7a7060] mt-0.5">
                    {professionalInfo.profession}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Global error */}
        {fieldErrors.global && (
          <div className="mb-3 text-[12.5px] px-3 py-2 rounded-lg border bg-[#f2e9e6] border-[#e6d2cb] text-[#9a564a]">
            {fieldErrors.global}
          </div>
        )}

        {/* Full Name — REQUIRED */}
        <div className="mb-[14px]">
          <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">
            Full Name *
          </div>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7a7060] opacity-60" />
            <input
              type="text"
              placeholder="Your full name"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                if (fieldErrors.fullName)
                  setFieldErrors((p) => ({ ...p, fullName: "" }));
              }}
              required
              className="w-full border-[1.5px] rounded-[9px] pl-9 pr-3 py-[10px] text-[12.5px] outline-none"
              style={{
                borderColor: fieldErrors.fullName ? "#e6d2cb" : "#ddd6c6",
              }}
            />
          </div>
          {fieldErrors.fullName && (
            <p className="mt-1 text-[11px] text-[#9a564a]">
              {fieldErrors.fullName}
            </p>
          )}
        </div>

        {/* Profession — REQUIRED */}
        <div className="mb-[14px]">
          <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">
            Profession *
          </div>
          <Select
            value={profession}
            onChange={(v) => {
              setProfession(v);
              if (fieldErrors.profession)
                setFieldErrors((p) => ({ ...p, profession: "" }));
            }}
            invalid={Boolean(fieldErrors.profession)}
            leftIcon={<Briefcase className="w-4 h-4 opacity-60" />}
            placeholder="Select your profession"
            options={PROFESSION_OPTIONS}
          />
          {fieldErrors.profession && (
            <p className="mt-1 text-[11px] text-[#9a564a]">
              {fieldErrors.profession}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="mb-[14px]">
          <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">
            Password *
          </div>
          <div className="relative">
            <input
              type={showPasswordToggle ? "text" : "password"}
              placeholder="Min. 8 characters"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password)
                  setFieldErrors((p) => ({ ...p, password: "" }));
              }}
              required
              minLength={8}
              className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none pr-10"
              style={{
                borderColor: fieldErrors.password ? "#e6d2cb" : "#ddd6c6",
              }}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[14px] opacity-50 hover:opacity-100"
              onClick={() => setShowPasswordToggle((p) => !p)}
            >
              {showPasswordToggle ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {fieldErrors.password && (
            <p className="mt-1 text-[11px] text-[#9a564a]">
              {fieldErrors.password}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="mb-[14px]">
          <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">
            Confirm Password *
          </div>
          <div className="relative">
            <input
              type={showConfirmToggle ? "text" : "password"}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (fieldErrors.confirmPassword)
                  setFieldErrors((p) => ({ ...p, confirmPassword: "" }));
              }}
              required
              minLength={8}
              className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none pr-10"
              style={{
                borderColor: fieldErrors.confirmPassword
                  ? "#e6d2cb"
                  : "#ddd6c6",
              }}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[14px] opacity-50 hover:opacity-100"
              onClick={() => setShowConfirmToggle((p) => !p)}
            >
              {showConfirmToggle ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {fieldErrors.confirmPassword && (
            <p className="mt-1 text-[11px] text-[#9a564a]">
              {fieldErrors.confirmPassword}
            </p>
          )}
        </div>

        {/* Password hint */}
        <div className="text-[10.5px] text-[#7a7060] mb-4 leading-relaxed">
          Must include: uppercase, lowercase, number, and special character
          (!@#$%...)
        </div>

        {/* Mobile Number */}
        <div className="mb-[14px]">
          <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">
            Mobile Number{" "}
            <span className="font-normal normal-case tracking-normal text-[#9a9080]">
              (Optional)
            </span>
          </div>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7a7060] opacity-60" />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="9876543210"
              value={mobile}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                setMobile(v);
                if (fieldErrors.mobile)
                  setFieldErrors((p) => ({ ...p, mobile: "" }));
              }}
              className="w-full border-[1.5px] rounded-[9px] pl-9 pr-3 py-[10px] text-[12.5px] outline-none"
              style={{
                borderColor: fieldErrors.mobile ? "#e6d2cb" : "#ddd6c6",
              }}
            />
          </div>
          {fieldErrors.mobile && (
            <p className="mt-1 text-[11px] text-[#9a564a]">
              {fieldErrors.mobile}
            </p>
          )}
          <p className="mt-1 text-[10.5px] text-[#7a7060]">
            For important notifications and account security
          </p>
        </div>

        {/* Membership Number */}
        <div className="mb-[14px]">
          <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">
            Membership Number{" "}
            <span className="font-normal normal-case tracking-normal text-[#9a9080]">
              (Optional)
            </span>
          </div>
          <div className="relative">
            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7a7060] opacity-60" />
            <input
              type="text"
              placeholder="Professional registration number"
              value={membershipNo}
              onChange={(e) => setMembershipNo(e.target.value)}
              className="w-full border-[1.5px] rounded-[9px] pl-9 pr-3 py-[10px] text-[12.5px] outline-none"
              style={{ borderColor: "#ddd6c6" }}
            />
          </div>
        </div>

        {/* Submit */}
        <Button type="submit" block size="lg" loading={busy}>
          {busy ? "Setting up your account…" : "Complete Setup"}
        </Button>

        <div className="text-center mt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/login")}
          >
            &larr; Back to Login
          </Button>
        </div>

        <div className="text-center text-[10.5px] text-[#9a9080] mt-4">
          By completing this setup, you agree to our Terms of Service and
          Privacy Policy.
        </div>
      </form>
    </Shell>
  );
};

export default AcceptInvitationPage;
